import * as React from 'react';
import { errorCodeToAnalyticsErrorCode, fireErrorEvent } from '../../../services/analytics';
import { getForgotEmailUrl } from '../../services/urlService';
import { sanitise } from '../../services/username';
import { login, userExistsInAuth0 } from 'ts/services/login/LoginService';
import {
  getUsernameValidationError,
  getPasswordValidationError,
} from 'ts/services/login/LoginValidationService';
import { handleLoginError } from 'ts/services/login/LoginErrorHandler';
import { LoginProps, LoginState, WrappedLoginComponentProps } from './interfaces';

// HOC that provides all the functionality for login
// It will manage the state & handlers for you
// To use this component, create your view and wrap it with this component
// You will then have access to props (WrappedLoginComponentProps) that you
// should pass through to your view
// See LoginPage & QuickLoginForm for example
const withLogin = (Component: React.ComponentType<WrappedLoginComponentProps>) =>
  class WithLogin extends React.Component<LoginProps, LoginState> {
    constructor(props: LoginProps) {
      super(props);
      this.state = {
        checkingUser: false,
        // TODO - This field can probably be derived
        disableUsernameIcon: false,
        error: null,
        loading: false,
        password: '',
        passwordError: null,
        rememberMe: true,
        usernameError: null,
        usernameNotFound: false,
      };
    }

    public render() {
      const {
        error,
        loading,
        password,
        rememberMe,
        usernameError,
        passwordError,
        checkingUser,
        disableUsernameIcon,
      } = this.state;
      return (
        <Component
          checkingUser={checkingUser}
          error={error}
          loading={loading}
          password={password}
          rememberMe={rememberMe}
          usernameError={usernameError}
          passwordError={passwordError}
          disableUsernameIcon={disableUsernameIcon}
          loginSubmit={this.submit}
          handleUsernameOnChange={this.handleUsernameOnChange}
          handleUsernameOnBlur={this.handleUsernameOnBlur}
          handlePasswordOnChange={this.handlePasswordOnChange}
          handleRememberMeChange={this.handleRememberMeChange}
          checkForValidUser={this.checkForValidUser}
          handleError={this.handleError}
          {...this.props}
        />
      );
    }

    private handleUsernameOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      this.setState({ passwordError: null, usernameError: null, disableUsernameIcon: true });
      const username = sanitise(e.currentTarget.value);
      const { setUsername } = this.props;
      if (setUsername) {
        setUsername(username);
      }
      this.isInvalidUsername(username);
    };

    private handleUsernameOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only do this check if the email is a valid syntax.
      const username = sanitise(e.currentTarget.value);
      const { setUsername } = this.props;
      if (setUsername) {
        setUsername(username);
      }
      this.checkForValidUser(username);
    };

    private checkIsInvalidPassword = (password: string): boolean => {
      const passwordError = getPasswordValidationError(password);
      this.setState({ passwordError, password });
      return !!passwordError;
    };

    private isInvalidUsername = (username: string) => {
      const usernameError = getUsernameValidationError(username);
      if (usernameError) {
        this.setState({ usernameError, disableUsernameIcon: false });
      }
      return usernameError;
    };

    private checkForValidUser = async (username: string) => {
      if (!this.isInvalidUsername(username) && this.props.isUserNameCheckEnabled) {
        this.setState({ checkingUser: true, disableUsernameIcon: false, usernameNotFound: false });
        try {
          if (await userExistsInAuth0(username)) {
            this.setState({ checkingUser: false, usernameNotFound: false });
          } else {
            fireErrorEvent('my-account:invalid-username', 'my-account:invalid-username');
            this.setState({
              usernameNotFound: true,
              usernameError: `Sorry, we couldn't find an account with this email. \n[Forgot your email?](${getForgotEmailUrl()})`,
              disableUsernameIcon: false,
              checkingUser: false,
            });
            if (this.state.passwordError) {
              this.setState({ passwordError: null });
            }
          }
        } catch (error) {
          this.setState({
            usernameError: null,
            usernameNotFound: false,
            disableUsernameIcon: true,
            checkingUser: false,
          });
        }
      }
    };

    // TODO - give this handler an argument and return type
    private handleRememberMeChange = (): void => {
      const { rememberMe } = this.state;
      this.setState({ rememberMe: !rememberMe });
    };

    private handlePasswordOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      this.checkIsInvalidPassword(e.currentTarget.value);
    };

    private handleError = (errorCode: string, usernameOrState: string) => {
      fireErrorEvent(
        errorCodeToAnalyticsErrorCode(errorCode),
        errorCodeToAnalyticsErrorCode(errorCode),
      );

      const loginError = handleLoginError(errorCode, usernameOrState);
      if (loginError.passwordError) {
        this.setState({
          passwordError: loginError.passwordError,
        });
      } else if (loginError.alertError) {
        this.setState({
          error: loginError.alertError,
        });
      } else {
        this.props.errorPageFunction(errorCode, this.props.history);
      }
    };

    private submit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const username = sanitise(this.props.username);
      const { setUsername } = this.props;
      if (setUsername) {
        setUsername(username);
      }
      const { password, usernameNotFound, rememberMe } = this.state;
      const isInvalidPassword = this.checkIsInvalidPassword(password);
      const isInvalidUsername = this.isInvalidUsername(username);
      if (isInvalidUsername || isInvalidPassword || usernameNotFound) {
        return;
      }

      this.setState({ loading: true, error: null });
      try {
        await login(username, password, rememberMe);
      } catch (error) {
        this.setState({ loading: false });
        this.handleError(error.message, username);
      }
    };
  };

export { withLogin };
